import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import ViewServiceModal from 'src/sections/Services/ViewServiceModal';
import EditServiceModal from 'src/sections/Services/EditServiceModal';
import ChangeStatusModal from 'src/sections/Services/ChangeStatusModal';

const SERVICES_DATA = Array.from({ length: 42 }, (_, index) => ({
  id: index + 1,
  company: index % 2 === 0 ? `Micromata GmbH ${index + 1}` : `Alvaro Pedro ${index + 1}`,
  product_name: index % 2 === 0 ? `Cloud Hosting` : `Security Firewall`,
  created_on: '2024-02-10',
  status: index % 3 === 0 ? 'active' : index % 3 === 1 ? 'pending' : 'deleted'
}));

export default function Services() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const filteredData = useMemo(() => {
    return SERVICES_DATA.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch =
        item.company.toLowerCase().includes(searchTerm) ||
        item.product_name.toLowerCase().includes(searchTerm);
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, statusFilter]);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleAction = (type, row) => {
    setSelectedRow(row);
    if (type === 'view') setViewOpen(true);
    if (type === 'edit') setEditOpen(true);
    if (type === 'status') setStatusOpen(true);
  };

  const tableHeaders = ['Company', 'Product Name', 'Created On', 'Status', 'Actions'];

  return (
    <Page title="Services">
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h4" sx={{ mb: 3, color: '#fff' }}>
            Services
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                placeholder="Search by company or product..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchQuery('');
                        setPage(1);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }}
                sx={{
                  bgcolor: '#161C24',
                  borderRadius: 1,
                  '& fieldset': { borderColor: '#333' },
                  input: { color: '#fff' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ bgcolor: '#161C24' }}>
                <InputLabel sx={{ color: '#9CA3AF' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '.MuiSvgIcon-root': { color: '#9CA3AF' }
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                sx={{ height: 56, bgcolor: '#3F90DF', fontWeight: 'bold' }}
              >
                Search
              </Button>
            </Grid>
          </Grid>

          <Card sx={{ bgcolor: '#212B36', border: '1px solid #333', borderRadius: 2 }}>
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.12)' }}>
                  <TableRow>
                    {tableHeaders.map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          color: '#9CA3AF',
                          borderBottom: '1px solid #333',
                          fontWeight: 'bold'
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                    >
                      <TableCell sx={{ color: '#fff' }}>{row.company}</TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>{row.product_name}</TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>{row.created_on}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 'bold',
                            bgcolor:
                              row.status === 'active'
                                ? 'rgba(84, 214, 44, 0.16)'
                                : row.status === 'pending'
                                ? 'rgba(255, 171, 0, 0.16)'
                                : 'rgba(255, 72, 66, 0.16)',
                            color:
                              row.status === 'active'
                                ? '#54D62C'
                                : row.status === 'pending'
                                ? '#FFAB00'
                                : '#FF4842'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => handleAction('view', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="carbon:view" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleAction('edit', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="fluent:edit-20-regular" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Status">
                            <IconButton
                              onClick={() => handleAction('status', row)}
                              sx={{ color: '#3F90DF' }}
                            >
                              <Iconify icon="ic:baseline-sync-alt" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/*PAGINATION */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #333'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ border: '1px solid #333', borderRadius: 1, color: '#9CA3AF' }}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 1 && pageNum <= page + 1)
                  ) {
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: 36,
                          height: 36,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: page === pageNum ? '#3F90DF' : '#333',
                          bgcolor: page === pageNum ? 'rgba(0, 110, 230, 0.1)' : 'transparent',
                          color: page === pageNum ? '#3F90DF' : '#fff'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                })}

                <IconButton
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage(page + 1)}
                  sx={{ border: '1px solid #333', borderRadius: 1, color: '#9CA3AF' }}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Container>

      {/* Modals called */}
      <ViewServiceModal open={viewOpen} onClose={() => setViewOpen(false)} data={selectedRow} />
      <EditServiceModal open={editOpen} onClose={() => setEditOpen(false)} data={selectedRow} />
      <ChangeStatusModal
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        data={selectedRow}
      />
    </Page>
  );
}
